import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { subDays, format, parseISO } from 'date-fns';
import { useMediaQuery } from 'react-responsive';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Box, Container, Typography, Paper, Button, Chip, CircularProgress,
  Alert, Skeleton, Tabs, Tab, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, TableSortLabel, TablePagination,
  TextField, Select, MenuItem, FormControl, InputLabel, Drawer,
  Divider, IconButton, Badge, Dialog, DialogTitle, DialogContent,
  DialogActions,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';

function toDateStr(d) {
  return format(d, 'yyyy-MM-dd');
}

function fmtDateTime(val) {
  if (!val) return '—';
  try {
    const d = typeof val === 'string' ? parseISO(val) : new Date(val);
    return format(d, 'MMM d, yyyy h:mm a') + ' UTC';
  } catch { return String(val); }
}

function fmtDate(val) {
  if (!val) return '—';
  try {
    const d = typeof val === 'string' ? parseISO(val) : new Date(val);
    return format(d, 'MMM d, yyyy');
  } catch { return String(val); }
}

function fmtWeek(val) {
  if (!val) return '';
  try {
    const d = typeof val === 'string' ? parseISO(val) : new Date(val);
    return format(d, 'MMM d');
  } catch { return String(val); }
}

function fmtCurrency(val) {
  if (val === null || val === undefined) return '—';
  return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtMinutes(val) {
  if (val === null || val === undefined) return '—';
  const n = Number(val);
  if (isNaN(n)) return '—';
  if (n >= 60) return `${Math.floor(n / 60)}h ${n % 60}m`;
  return `${n}m`;
}

function StatusChip({ status }) {
  const map = {
    pending: { label: 'Pending', color: 'default' },
    resolved: { label: 'Resolved', color: 'success' },
    alerted: { label: 'Alerted', color: 'error' },
    manually_resolved: { label: 'Manual Fix', color: 'primary' },
  };
  const { label, color } = map[status] || { label: status, color: 'default' };
  return <Chip label={label} color={color} size="small" />;
}

function TierChip({ tier }) {
  if (!tier) return null;
  const map = {
    tier_1: { label: 'Exact Tab Match', color: 'success' },
    tier_2: { label: 'Tab Contains', color: 'primary' },
    tier_3: { label: 'Heuristic', color: 'warning' },
  };
  const { label, color } = map[tier] || { label: tier, color: 'default' };
  return <Chip label={label} color={color} size="small" />;
}

function KpiCard({ label, value, sub, loading }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 130, flex: '1 1 130px' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      {loading ? (
        <Skeleton variant="text" width={60} height={36} />
      ) : (
        <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2, mt: 0.5 }}>
          {value ?? '—'}
        </Typography>
      )}
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function sortRows(rows, orderBy, order) {
  return [...rows].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });
}

export default function EzCaterDashboard() {
  const { user: firebaseUser } = useUserAuth();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });

  const defaultFrom = toDateStr(subDays(new Date(), 30));
  const defaultTo = toDateStr(new Date());

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(defaultTo);
  const [tab, setTab] = useState(0);

  const [kpis, setKpis] = useState(null);
  const [loadingKpis, setLoadingKpis] = useState(false);
  const [kpisError, setKpisError] = useState(null);

  const [timeseries, setTimeseries] = useState([]);
  const [loadingTimeseries, setLoadingTimeseries] = useState(false);

  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [storeOrderBy, setStoreOrderBy] = useState('FAILURE_PCT');
  const [storeOrder, setStoreOrder] = useState('desc');

  const [openAlerts, setOpenAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const alertTimerRef = useRef(null);

  const [ordersResult, setOrdersResult] = useState({ total: 0, rows: [] });
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersPageSize, setOrdersPageSize] = useState(50);
  const [orderFilters, setOrderFilters] = useState({
    status: '', storeNumber: '', search: '', dateFrom: defaultFrom, dateTo: defaultTo,
  });
  const [pendingFilters, setPendingFilters] = useState({
    status: '', storeNumber: '', search: '', dateFrom: defaultFrom, dateTo: defaultTo,
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState(null);
  const [loadingDrawer, setLoadingDrawer] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectError, setRejectError] = useState(null);

  const getToken = useCallback(async () => {
    if (!firebaseUser?.getIdToken) return null;
    return firebaseUser.getIdToken(true);
  }, [firebaseUser]);

  const loadKpis = useCallback(async (from, to) => {
    const token = await getToken();
    if (!token) return;
    setLoadingKpis(true);
    setKpisError(null);
    try {
      const { data } = await axios.get(`/hd-api/ezcater/kpis?dateFrom=${from}&dateTo=${to}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKpis(data);
    } catch (e) {
      setKpisError(e.response?.data?.error || 'Failed to load KPIs');
    } finally {
      setLoadingKpis(false);
    }
  }, [getToken]);

  const loadTimeseries = useCallback(async (from, to) => {
    const token = await getToken();
    if (!token) return;
    setLoadingTimeseries(true);
    try {
      const { data } = await axios.get(`/hd-api/ezcater/timeseries?dateFrom=${from}&dateTo=${to}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTimeseries(data);
    } catch { } finally {
      setLoadingTimeseries(false);
    }
  }, [getToken]);

  const loadStores = useCallback(async (from, to) => {
    const token = await getToken();
    if (!token) return;
    setLoadingStores(true);
    try {
      const { data } = await axios.get(`/hd-api/ezcater/stores?dateFrom=${from}&dateTo=${to}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStores(data);
    } catch { } finally {
      setLoadingStores(false);
    }
  }, [getToken]);

  const loadAlerts = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setLoadingAlerts(true);
    try {
      const { data } = await axios.get('/hd-api/ezcater/alerts/open', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpenAlerts(data);
    } catch { } finally {
      setLoadingAlerts(false);
    }
  }, [getToken]);

  const loadOrders = useCallback(async (filters, page, pageSize) => {
    const token = await getToken();
    if (!token) return;
    setLoadingOrders(true);
    try {
      const params = new URLSearchParams({ page: page + 1, pageSize });
      if (filters.status) params.set('status', filters.status);
      if (filters.storeNumber) params.set('storeNumber', filters.storeNumber);
      if (filters.search) params.set('search', filters.search);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      const { data } = await axios.get(`/hd-api/ezcater/orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrdersResult(data);
    } catch { } finally {
      setLoadingOrders(false);
    }
  }, [getToken]);

  const handleExportOrders = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setExporting(true);
    setExportError(null);
    try {
      const params = new URLSearchParams({ export: '1' });
      if (orderFilters.status) params.set('status', orderFilters.status);
      if (orderFilters.storeNumber) params.set('storeNumber', orderFilters.storeNumber);
      if (orderFilters.search) params.set('search', orderFilters.search);
      if (orderFilters.dateFrom) params.set('dateFrom', orderFilters.dateFrom);
      if (orderFilters.dateTo) params.set('dateTo', orderFilters.dateTo);
      const { data } = await axios.get(`/hd-api/ezcater/orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!data.rows || data.rows.length === 0) {
        setExportError('No orders match the current filters.');
        return;
      }

      const exportRows = data.rows.map(r => ({
        'Order #': r.ORDER_NUMBER || '',
        'Store': r.STORE_NUMBER || '',
        'Status': r.STATUS || '',
        'Order Total': r.ORDER_TOTAL !== null && r.ORDER_TOTAL !== undefined ? Number(r.ORDER_TOTAL) : '',
        'Delivery At (UTC)': r.DELIVERY_AT || '',
        'Received At (UTC)': r.RECEIVED_AT || '',
        'Contact Name': r.CONTACT_NAME || '',
        'Contact Phone': r.CONTACT_PHONE || '',
        'Manual Toast GUID': r.MANUAL_TOAST_GUID || '',
        'Manual Match Tier': r.MANUAL_MATCH_TIER || '',
        'Manually Resolved At (UTC)': r.MANUALLY_RESOLVED_AT || '',
        'ezCater Order ID': r.EZCATER_ORDER_ID || '',
      }));

      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'ezCater Orders');

      const statusTag = orderFilters.status ? `_${orderFilters.status}` : '';
      const storeTag = orderFilters.storeNumber ? `_store${orderFilters.storeNumber}` : '';
      const filename = `ezcater-orders_${orderFilters.dateFrom}_to_${orderFilters.dateTo}${statusTag}${storeTag}.xlsx`;
      XLSX.writeFile(wb, filename);

      if (data.truncated) {
        setExportError(`Export capped at ${data.cap.toLocaleString()} rows — narrow your filters to see the rest.`);
      }
    } catch (e) {
      setExportError(e.response?.data?.error || 'Export failed.');
    } finally {
      setExporting(false);
    }
  }, [orderFilters, getToken]);

  const handleExportAlerts = useCallback(() => {
    setExportError(null);
    if (!openAlerts.length) {
      setExportError('No open alerts to export.');
      return;
    }
    try {
      const exportRows = openAlerts.map(r => ({
        'Order #': r.ORDER_NUMBER || '',
        'Store': r.STORE_NUMBER || '',
        'Delivery At (UTC)': r.DELIVERY_AT || '',
        'Contact Name': r.CONTACT_NAME || '',
        'Contact Phone': r.CONTACT_PHONE || '',
        'Order Total': r.ORDER_TOTAL !== null && r.ORDER_TOTAL !== undefined ? Number(r.ORDER_TOTAL) : '',
        'Alerted At (UTC)': r.ALERTED_AT || '',
        'Hours Since Alert': r.HOURS_SINCE_ALERT != null ? Number(r.HOURS_SINCE_ALERT) : '',
        'ezCater Order ID': r.EZCATER_ORDER_ID || '',
      }));
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Open Alerts');
      const stamp = format(new Date(), 'yyyy-MM-dd_HHmm');
      XLSX.writeFile(wb, `ezcater-open-alerts_${stamp}.xlsx`);
    } catch (e) {
      setExportError('Export failed.');
    }
  }, [openAlerts]);

  const loadOrderDetail = useCallback(async (uuid) => {
    const token = await getToken();
    if (!token) return;
    setDrawerOpen(true);
    setDrawerData(null);
    setLoadingDrawer(true);
    try {
      const { data } = await axios.get(`/hd-api/ezcater/orders/${uuid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDrawerData(data);
    } catch { } finally {
      setLoadingDrawer(false);
    }
  }, [getToken]);

  const handleRejectMatch = useCallback(async () => {
    if (!drawerData?.EZCATER_ORDER_ID) return;
    const token = await getToken();
    if (!token) return;
    setRejecting(true);
    setRejectError(null);
    try {
      const { data } = await axios.post(
        `/hd-api/ezcater/orders/${drawerData.EZCATER_ORDER_ID}/reject-match`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDrawerData(data);
      setRejectDialogOpen(false);
    } catch (e) {
      setRejectError(e.response?.data?.error || 'Failed to reject match');
    } finally {
      setRejecting(false);
    }
  }, [drawerData, getToken]);

  useEffect(() => {
    loadKpis(dateFrom, dateTo);
    loadTimeseries(dateFrom, dateTo);
    loadStores(dateFrom, dateTo);
  }, [dateFrom, dateTo, loadKpis, loadTimeseries, loadStores]);

  useEffect(() => {
    loadAlerts();
    alertTimerRef.current = setInterval(loadAlerts, 5 * 60 * 1000);
    return () => clearInterval(alertTimerRef.current);
  }, [loadAlerts]);

  useEffect(() => {
    loadOrders(orderFilters, ordersPage, ordersPageSize);
  }, [orderFilters, ordersPage, ordersPageSize, loadOrders]);

  function handleStoreSort(col) {
    if (col === storeOrderBy) {
      setStoreOrder(o => o === 'asc' ? 'desc' : 'asc');
    } else {
      setStoreOrderBy(col);
      setStoreOrder('desc');
    }
  }

  function handleOrdersSearch() {
    setOrdersPage(0);
    setOrderFilters({ ...pendingFilters });
  }

  const sortedStores = sortRows(stores, storeOrderBy, storeOrder);

  const resolvedPct = kpis?.TOTAL > 0
    ? ` (${((kpis.RESOLVED / kpis.TOTAL) * 100).toFixed(1)}%)`
    : '';

  const storeCols = [
    { id: 'STORE_NUMBER', label: 'Store #' },
    { id: 'TOTAL', label: 'Total' },
    { id: 'FAILED', label: 'Failed' },
    { id: 'FAILURE_PCT', label: 'Failure Rate' },
    { id: 'MANUALLY_CORRECTED', label: 'Manually Corrected' },
    { id: 'CORRECTION_PCT', label: 'Correction Rate' },
    { id: 'MED_MIN_TO_CORRECT', label: 'Med. Time to Fix' },
  ];

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth="xl">
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          {/* Page header */}
          <Paper
            elevation={0}
            sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 2 }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderLeft: (theme) => `4px solid ${theme.palette.warning.main}`,
                px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
              }}
            >
              <Box
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.1),
                  borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <NotificationsActiveIcon sx={{ fontSize: 32, color: 'warning.main' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Lato', color: 'text.primary' }}>
                  ezCater Alerts
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Monitor ezCater orders that failed to auto-ingest into Olo
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  type="date"
                  size="small"
                  label="From"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                />
                <TextField
                  type="date"
                  size="small"
                  label="To"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 150 }}
                />
              </Box>
            </Box>
          </Paper>

          {kpisError && <Alert severity="error" sx={{ mb: 2 }}>{kpisError}</Alert>}

          {/* Tabs */}
          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
            >
              <Tab label="Overview" />
              <Tab label="Stores" />
              <Tab
                label={
                  <Badge badgeContent={openAlerts.length || null} color="error">
                    <Box sx={{ pr: openAlerts.length ? 1.5 : 0 }}>Open Alerts</Box>
                  </Badge>
                }
              />
              <Tab label="All Orders" />
            </Tabs>

            <Box sx={{ p: 2 }}>

              {/* ── Overview ──────────────────────────────────────────────── */}
              {tab === 0 && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    <KpiCard label="Total Orders" value={kpis?.TOTAL} loading={loadingKpis} />
                    <KpiCard
                      label="Auto-Resolved"
                      value={kpis?.RESOLVED}
                      sub={resolvedPct}
                      loading={loadingKpis}
                    />
                    <KpiCard label="Failed Ingest" value={kpis?.TOTAL_FAILURES} loading={loadingKpis} />
                    <KpiCard label="Manually Corrected" value={kpis?.MANUALLY_RESOLVED} loading={loadingKpis} />
                    <KpiCard
                      label="Correction Rate"
                      value={kpis?.MANUAL_CORRECT_PCT != null ? `${kpis.MANUAL_CORRECT_PCT}%` : '—'}
                      loading={loadingKpis}
                    />
                    <KpiCard
                      label="Median Time to Fix"
                      value={fmtMinutes(kpis?.MED_MIN_TO_CORRECT)}
                      loading={loadingKpis}
                    />
                  </Box>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} mb={2}>Weekly Order Volume</Typography>
                    {loadingTimeseries ? (
                      <Skeleton variant="rectangular" height={280} />
                    ) : (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart
                          data={timeseries.map(w => ({ ...w, week: fmtWeek(w.WEEK) }))}
                          margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <RechartsTooltip />
                          <Legend />
                          <Bar dataKey="RESOLVED" name="Auto-Resolved" stackId="a" fill="#2e7d32" />
                          <Bar dataKey="MANUAL" name="Manually Corrected" stackId="a" fill="#1565c0" />
                          <Bar dataKey="OPEN_ALERTS" name="Still Open" stackId="a" fill="#c62828" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Paper>
                </Box>
              )}

              {/* ── Stores ────────────────────────────────────────────────── */}
              {tab === 1 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {storeCols.map(col => (
                          <TableCell key={col.id}>
                            <TableSortLabel
                              active={storeOrderBy === col.id}
                              direction={storeOrderBy === col.id ? storeOrder : 'asc'}
                              onClick={() => handleStoreSort(col.id)}
                            >
                              {col.label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingStores
                        ? Array.from({ length: 6 }).map((_, i) => (
                          <TableRow key={i}>
                            {storeCols.map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}
                          </TableRow>
                        ))
                        : sortedStores.length === 0
                          ? (
                            <TableRow>
                              <TableCell colSpan={storeCols.length} align="center">
                                <Typography variant="body2" color="text.secondary" py={2}>No data</Typography>
                              </TableCell>
                            </TableRow>
                          )
                          : sortedStores.map(row => (
                            <TableRow key={row.STORE_NUMBER} hover>
                              <TableCell>{row.STORE_NUMBER}</TableCell>
                              <TableCell>{row.TOTAL}</TableCell>
                              <TableCell>{row.FAILED}</TableCell>
                              <TableCell>{row.FAILURE_PCT != null ? `${row.FAILURE_PCT}%` : '—'}</TableCell>
                              <TableCell>{row.MANUALLY_CORRECTED}</TableCell>
                              <TableCell>{row.FAILED > 0 ? `${row.CORRECTION_PCT ?? '—'}%` : '—'}</TableCell>
                              <TableCell>{fmtMinutes(row.MED_MIN_TO_CORRECT)}</TableCell>
                            </TableRow>
                          ))
                      }
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* ── Open Alerts ───────────────────────────────────────────── */}
              {tab === 2 && (
                <Box>
                  {!loadingAlerts && openAlerts.length === 0 ? (
                    <Alert severity="success">No open alerts — all ezCater orders are accounted for.</Alert>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FileDownloadIcon />}
                          onClick={handleExportAlerts}
                          disabled={loadingAlerts || openAlerts.length === 0}
                        >
                          Export XLSX
                        </Button>
                      </Box>
                      {exportError && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
                          {exportError}
                        </Alert>
                      )}
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Order #</TableCell>
                            <TableCell>Store</TableCell>
                            <TableCell>Delivery Time</TableCell>
                            <TableCell>Contact</TableCell>
                            <TableCell>Order Total</TableCell>
                            <TableCell>Alert Age</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loadingAlerts
                            ? Array.from({ length: 4 }).map((_, i) => (
                              <TableRow key={i}>
                                {Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}
                              </TableRow>
                            ))
                            : openAlerts.map(row => (
                              <TableRow
                                key={row.EZCATER_ORDER_ID}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => loadOrderDetail(row.EZCATER_ORDER_ID)}
                              >
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                  {row.ORDER_NUMBER || '—'}
                                </TableCell>
                                <TableCell>{row.STORE_NUMBER}</TableCell>
                                <TableCell>{fmtDateTime(row.DELIVERY_AT)}</TableCell>
                                <TableCell>
                                  <Typography variant="body2">{row.CONTACT_NAME || '—'}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {row.CONTACT_PHONE || ''}
                                  </Typography>
                                </TableCell>
                                <TableCell>{fmtCurrency(row.ORDER_TOTAL)}</TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: row.HOURS_SINCE_ALERT > 24 ? 'error.main' : 'text.primary' }}
                                  >
                                    {row.HOURS_SINCE_ALERT != null ? `${row.HOURS_SINCE_ALERT}h ago` : '—'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))
                          }
                        </TableBody>
                      </Table>
                    </TableContainer>
                    </>
                  )}
                </Box>
              )}

              {/* ── All Orders ────────────────────────────────────────────── */}
              {tab === 3 && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, alignItems: 'flex-end' }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={pendingFilters.status}
                        onChange={e => setPendingFilters(f => ({ ...f, status: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="resolved">Resolved</MenuItem>
                        <MenuItem value="alerted">Alerted</MenuItem>
                        <MenuItem value="manually_resolved">Manually Resolved</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Store Number"
                      value={pendingFilters.storeNumber}
                      onChange={e => setPendingFilters(f => ({ ...f, storeNumber: e.target.value }))}
                      sx={{ width: 140 }}
                    />
                    <TextField
                      size="small"
                      label="Order Number"
                      value={pendingFilters.search}
                      onChange={e => setPendingFilters(f => ({ ...f, search: e.target.value }))}
                      sx={{ width: 150 }}
                    />
                    <TextField
                      type="date"
                      size="small"
                      label="From"
                      value={pendingFilters.dateFrom}
                      onChange={e => setPendingFilters(f => ({ ...f, dateFrom: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150 }}
                    />
                    <TextField
                      type="date"
                      size="small"
                      label="To"
                      value={pendingFilters.dateTo}
                      onChange={e => setPendingFilters(f => ({ ...f, dateTo: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150 }}
                    />
                    <Button variant="contained" size="small" onClick={handleOrdersSearch}>
                      Search
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      onClick={handleExportOrders}
                      disabled={exporting || loadingOrders}
                    >
                      {exporting ? 'Exporting…' : 'Export XLSX'}
                    </Button>
                  </Box>

                  {exportError && (
                    <Alert
                      severity={exportError.startsWith('Export capped') ? 'warning' : 'error'}
                      sx={{ mb: 2 }}
                      onClose={() => setExportError(null)}
                    >
                      {exportError}
                    </Alert>
                  )}

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Order #</TableCell>
                          <TableCell>Store</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Order Total</TableCell>
                          <TableCell>Delivery At</TableCell>
                          <TableCell>Received At</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loadingOrders
                          ? Array.from({ length: 8 }).map((_, i) => (
                            <TableRow key={i}>
                              {Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}
                            </TableRow>
                          ))
                          : ordersResult.rows.length === 0
                            ? (
                              <TableRow>
                                <TableCell colSpan={6} align="center">
                                  <Typography variant="body2" color="text.secondary" py={2}>No orders found</Typography>
                                </TableCell>
                              </TableRow>
                            )
                            : ordersResult.rows.map(row => (
                              <TableRow
                                key={row.EZCATER_ORDER_ID}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => loadOrderDetail(row.EZCATER_ORDER_ID)}
                              >
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                  {row.ORDER_NUMBER || '—'}
                                </TableCell>
                                <TableCell>{row.STORE_NUMBER}</TableCell>
                                <TableCell><StatusChip status={row.STATUS} /></TableCell>
                                <TableCell>{fmtCurrency(row.ORDER_TOTAL)}</TableCell>
                                <TableCell>{fmtDate(row.DELIVERY_AT)}</TableCell>
                                <TableCell>{fmtDate(row.RECEIVED_AT)}</TableCell>
                              </TableRow>
                            ))
                        }
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={ordersResult.total}
                    page={ordersPage}
                    onPageChange={(_, p) => setOrdersPage(p)}
                    rowsPerPage={ordersPageSize}
                    onRowsPerPageChange={e => { setOrdersPageSize(parseInt(e.target.value)); setOrdersPage(0); }}
                    rowsPerPageOptions={[25, 50, 100]}
                  />
                </Box>
              )}

            </Box>
          </Paper>
        </Box>
      </Container>

      {/* ── Order Detail Drawer ─────────────────────────────────────────────── */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 480, p: 3, height: '100%', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Order Detail</Typography>
            <IconButton size="small" onClick={() => setDrawerOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {loadingDrawer && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {!loadingDrawer && drawerData && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>ezCater Order</Typography>
              <DetailRow label="Order #" value={drawerData.ORDER_NUMBER} />
              <DetailRow label="Store" value={drawerData.STORE_NUMBER} />
              <DetailRow label="Total" value={fmtCurrency(drawerData.ORDER_TOTAL)} />
              <DetailRow label="Delivery" value={fmtDateTime(drawerData.DELIVERY_AT)} />
              <DetailRow label="Contact Name" value={drawerData.CONTACT_NAME} />
              <DetailRow label="Contact Phone" value={drawerData.CONTACT_PHONE} />
              {(drawerData.DELIVERY_STREET || drawerData.DELIVERY_CITY || drawerData.DELIVERY_ZIP) && (
                <DetailRow
                  label="Address"
                  value={[drawerData.DELIVERY_STREET, drawerData.DELIVERY_CITY, drawerData.DELIVERY_ZIP]
                    .filter(Boolean).join(', ')}
                />
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={600} mb={1.5}>Status Timeline</Typography>
              {[
                { label: 'Received', time: drawerData.RECEIVED_AT },
                { label: 'Alerted', time: drawerData.ALERTED_AT },
                { label: 'Manually Resolved', time: drawerData.MANUALLY_RESOLVED_AT },
              ].map(({ label, time }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.25 }}>
                  <Box sx={{
                    width: 10, height: 10, borderRadius: '50%', mt: '4px', flexShrink: 0,
                    bgcolor: time ? 'success.main' : 'action.disabled',
                  }} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {time ? fmtDateTime(time) : '(not yet)'}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {drawerData.MANUAL_TOAST_GUID && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={600}>Matched Toast Entry</Typography>
                    <TierChip tier={drawerData.MANUAL_MATCH_TIER} />
                  </Box>
                  <DetailRow label="Toast GUID" value={drawerData.MANUAL_TOAST_GUID} />
                  <DetailRow label="Receipt #" value={drawerData.MATCHED_TOAST_DISPLAY_NUMBER} />
                  <DetailRow label="Tab Name" value={drawerData.MATCHED_TOAST_TAB_NAME} />
                  <DetailRow label="Opened At" value={fmtDateTime(drawerData.MATCHED_TOAST_OPENED_AT)} />
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
                      Toast Total
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: drawerData.MATCHED_TOAST_TOTAL != null && drawerData.ORDER_TOTAL != null &&
                          Math.abs(drawerData.MATCHED_TOAST_TOTAL - drawerData.ORDER_TOTAL) > drawerData.ORDER_TOTAL * 0.10
                          ? 'warning.main' : 'text.primary',
                        fontWeight: drawerData.MATCHED_TOAST_TOTAL != null && drawerData.ORDER_TOTAL != null &&
                          Math.abs(drawerData.MATCHED_TOAST_TOTAL - drawerData.ORDER_TOTAL) > drawerData.ORDER_TOTAL * 0.10
                          ? 600 : 400,
                      }}
                    >
                      {fmtCurrency(drawerData.MATCHED_TOAST_TOTAL)}
                      {drawerData.MATCHED_TOAST_TOTAL != null && drawerData.ORDER_TOTAL != null && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          (ezCater: {fmtCurrency(drawerData.ORDER_TOTAL)})
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                </>
              )}

              {drawerData.STATUS === 'manually_resolved' && drawerData.MANUAL_TOAST_GUID && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => { setRejectError(null); setRejectDialogOpen(true); }}
                  >
                    Reject this match
                  </Button>
                </Box>
              )}

              {drawerData.ALERTED_AT && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>Alert Info</Typography>
                  <DetailRow label="Alerted At" value={fmtDateTime(drawerData.ALERTED_AT)} />
                  <DetailRow label="Alert Type" value={drawerData.ALERT_TYPE} />
                </>
              )}
            </Box>
          )}
        </Box>
      </Drawer>
      {/* ── Reject Match Confirmation Dialog ───────────────────────────────── */}
      <Dialog open={rejectDialogOpen} onClose={() => !rejecting && setRejectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject this match?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This will remove the current Toast match and flag the GUID so the matcher won't suggest it again.
          </Typography>
          {drawerData && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                  ezCater Order
                </Typography>
                <Typography variant="body2">{drawerData.ORDER_NUMBER || '—'}</Typography>
                <Typography variant="body2">{fmtCurrency(drawerData.ORDER_TOTAL)}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                  Toast Match
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <Typography variant="body2">{drawerData.MATCHED_TOAST_TAB_NAME || '—'}</Typography>
                  <TierChip tier={drawerData.MANUAL_MATCH_TIER} />
                </Box>
                <Typography variant="body2">{fmtCurrency(drawerData.MATCHED_TOAST_TOTAL)}</Typography>
                {drawerData.MATCHED_TOAST_DISPLAY_NUMBER && (
                  <Typography variant="caption" color="text.secondary">
                    Receipt #{drawerData.MATCHED_TOAST_DISPLAY_NUMBER}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          {rejectError && <Alert severity="error" sx={{ mt: 2 }}>{rejectError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={rejecting}>Cancel</Button>
          <Button onClick={handleRejectMatch} color="error" variant="contained" disabled={rejecting}>
            {rejecting ? 'Rejecting...' : 'Reject match'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
